
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Producto } from '@shared/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2Icon, SaveIcon, BanIcon } from '@/components/Icons';
import { toast } from 'sonner';
import { useTaxes } from '@/contexts/TaxContext';

export default function ProductoForm() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { taxes, isLoading: taxesLoading } = useTaxes();
    const defaultTax = taxes.find(t => t.isDefault);
    const [producto, setProducto] = useState<Partial<Producto>>({
        descripcion: '',
        precio: 0,
        taxId: defaultTax?.id
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            fetch(`/api/productos/${id}`)
                .then(res => {
                    if (!res.ok) throw new Error('Producto no encontrado');
                    return res.json();
                })
                .then(data => setProducto(data))
                .catch(error => {
                    toast.error('Error al cargar producto', { description: error.message });
                    navigate('/productos');
                })
                .finally(() => setIsLoading(false));
        } else if (defaultTax && !producto.taxId) {
            // Establecer el impuesto por defecto solo al crear un nuevo producto
            setProducto(prev => ({ ...prev, taxId: defaultTax.id }));
        }
    }, [id, navigate, defaultTax, producto.taxId]);

    const handleChange = (field: keyof Producto, value: string | number) => {
        setProducto(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!producto.descripcion?.trim()) {
            toast.warning('Error de validación', { description: 'La descripción es obligatoria.' });
            return;
        }
        const precio = Number(producto.precio);
        if (isNaN(precio) || precio < 0) {
            toast.warning('Error de validación', { description: 'El precio introducido no es válido.' });
            return;
        }

        setIsSaving(true);
        try {
            const response = await fetch(id ? `/api/productos/${id}` : '/api/productos', {
                method: id ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...producto, precio }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al guardar el producto');
            }
            
            toast.success(`Producto ${id ? 'actualizado' : 'creado'} con éxito`);
            navigate('/productos');

        } catch (error) {
            toast.error('Error al guardar', { description: error instanceof Error ? error.message : 'Ocurrió un error inesperado' });
        } finally {
            setIsSaving(false);
        }
    };
    
    if (isLoading && id) return <div className="flex justify-center items-center h-64"><Loader2Icon className="animate-spin h-8 w-8 text-primary" /></div>

    return (
        <div className="max-w-2xl mx-auto space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold tracking-tighter">{id ? 'Editar Producto o Servicio' : 'Nuevo Ítem'}</h1>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate('/productos')} disabled={isSaving}><BanIcon className="mr-2" />Cancelar</Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                        {isSaving ? <><Loader2Icon className="mr-2 h-4 w-4 animate-spin"/> Guardando...</> : <><SaveIcon className="mr-2" />Guardar</>}
                    </Button>
                </div>
            </div>

            <Card>
                <CardHeader><CardTitle>Detalles del Ítem</CardTitle></CardHeader>
                <CardContent className="space-y-6">
                    <div className="space-y-2">
                        <Label htmlFor="descripcion">Descripción</Label>
                        <Textarea id="descripcion" value={producto.descripcion ?? ''} onChange={(e) => handleChange('descripcion', e.target.value)} placeholder="Describe el producto o servicio" />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="precio">Precio unitario</Label>
                            <div className="flex items-center">
                                <Input id="precio" type="number" step="0.01" value={producto.precio ?? 0} onChange={(e) => handleChange('precio', parseFloat(e.target.value) || 0)} className="rounded-r-none" />
                                <span className="flex h-10 items-center rounded-r-md border border-l-0 border-input bg-muted px-3 text-muted-foreground text-sm">€</span>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="taxId">Impuesto</Label>
                            <Select
                                value={producto.taxId || ''}
                                onValueChange={(value) => handleChange('taxId', value)}
                                disabled={taxesLoading || taxes.length === 0}
                            >
                                <SelectTrigger id="taxId">
                                    <SelectValue placeholder={taxesLoading ? "Cargando..." : taxes.length === 0 ? "No hay impuestos" : "Selecciona impuesto"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {taxes.map(tax => (
                                        <SelectItem key={tax.id} value={tax.id}>
                                            {tax.nombre} ({(tax.rate * 100).toFixed(0)}%)
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="referencia">Referencia <span className="text-muted-foreground">(Opcional)</span></Label>
                            <Input id="referencia" value={producto.referencia ?? ''} onChange={(e) => handleChange('referencia', e.target.value)} placeholder="Código o referencia del producto" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="url">URL <span className="text-muted-foreground">(Opcional)</span></Label>
                            <Input id="url" type="url" value={producto.url ?? ''} onChange={(e) => handleChange('url', e.target.value)} placeholder="https://ejemplo.com/producto" />
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
